import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
import { useAuth } from "../context/AuthContext";
import loginBackground from "../assets/login-library.png";

// const STOREFRONT_URL = process.env.REACT_APP_STORE_URL || 'http://localhost:3000';

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, googleLogin, verifyTwoFactor, isAuthenticated } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  if (isAuthenticated) return <Navigate to="/" replace />;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);

    const result = await login(email.trim(), password, rememberMe);
    if (result.requiresTwoFactor) {
      setChallenge(result);
      setLoading(false);
      return;
    }
    if (result.success) {
      navigate(location.state?.from || "/", { replace: true });
      return;
    }

    setError(result.message || "Unable to sign in. Please try again.");
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    if (loading) return;
    setError("");
    setLoading(true);
    const result = await googleLogin(rememberMe);
    if (result.requiresTwoFactor) {
      setChallenge(result);
      setLoading(false);
      return;
    }
    if (result.success) {
      navigate(location.state?.from || "/", { replace: true });
      return;
    }
    setError(result.message || "Google sign-in failed.");
    setLoading(false);
  };

  return (
    <main className="flex min-h-screen bg-white lg:h-screen">
      <section
        className="relative hidden min-h-full w-1/2 bg-cover bg-center text-white lg:flex"
        style={{ backgroundImage: `url(${loginBackground})` }}
        aria-label="Ekamithila library"
      >
        <div className="absolute inset-0 bg-orange-500/75" />
        <div className="relative z-10 flex w-full flex-col justify-between px-14 py-16 xl:px-20">
          <span className="text-3xl font-bold tracking-tight">Ekamithila</span>
          <div className="max-w-lg pb-28">
            <h1 className="text-5xl font-bold leading-[1.03] tracking-tight xl:text-6xl">
              Discover Your
              <br />
              Next Great Read
            </h1>
            <p className="mt-5 max-w-md text-lg leading-8 text-white/95">
              Join thousands of book lovers and explore our curated collection
              of quality books.
            </p>
          </div>
          <span aria-hidden="true" />
        </div>
      </section>

      <section className="flex w-full items-center justify-center px-6 py-12 lg:w-1/2 lg:px-12">
        <div className="w-full max-w-[560px]">
          <header className="mb-10 text-center">
            <h2 className="mb-2 text-3xl font-bold text-orange-500 sm:text-4xl">
              Ekamithila Admin Panel
            </h2>
            <p className="text-base text-gray-900">
              Welcome back! Sign in to continue.
            </p>
          </header>

          {error && (
            <div
              role="alert"
              className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          {challenge ? (
            <form
              className="space-y-4"
              onSubmit={async (event) => {
                event.preventDefault();
                setLoading(true);
                setError("");
                const result = await verifyTwoFactor(
                  challenge.challengeId,
                  code,
                  rememberMe,
                );
                setLoading(false);
                if (result.success)
                  navigate(location.state?.from || "/", { replace: true });
                else setError(result.message);
              }}
            >
              <p>
                Enter the six-digit code sent to your account email. It expires
                in 5 minutes.
              </p>
              <label htmlFor="admin-otp">Verification code</label>
              <input
                id="admin-otp"
                className="block w-full border rounded-lg p-3"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                required
              />
              <button
                disabled={loading}
                className="bg-orange-500 text-white rounded-lg px-6 py-3"
              >
                {loading ? "Verifying..." : "Verify and sign in"}
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={() => {
                  setChallenge(null);
                  setCode("");
                  setError("");
                }}
                className="ml-3 underline"
              >
                Start again
              </button>
            </form>
          ) : (
            <>
              <form onSubmit={handleSubmit} noValidate>
                <label
                  htmlFor="admin-email"
                  className="mb-2 block font-semibold text-gray-950"
                >
                  Email Address
                </label>
                <input
                  id="admin-email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setError("");
                  }}
                  className="mb-6 w-full rounded-xl border border-gray-300 px-5 py-4 outline-none transition placeholder:text-gray-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  placeholder="Enter your email"
                />

                <label
                  htmlFor="admin-password"
                  className="mb-2 block font-semibold text-gray-950"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    id="admin-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    minLength={6}
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                      setError("");
                    }}
                    className="w-full rounded-xl border border-gray-300 px-5 py-4 pr-14 outline-none transition placeholder:text-gray-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((visible) => !visible)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 rounded p-1 text-xl text-gray-900 hover:text-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>

                <div className="my-5 flex items-center justify-between gap-4">
                  <label className="flex cursor-pointer items-center gap-2 text-gray-950">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(event) => setRememberMe(event.target.checked)}
                      className="h-5 w-5 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                    />
                    Remember Me
                  </label>
                  {/* <a href={`${STOREFRONT_URL}/forgot-password`} className="font-medium text-orange-500 hover:underline">
                Forgot Password?
              </a> */}
                  <a
                    href={`#}/forgot-password`}
                    className="font-medium text-orange-500 hover:underline"
                  >
                    Forgot Password?
                  </a>
                </div>

                <button
                  type="submit"
                  disabled={loading || !email.trim() || !password}
                  className="w-full rounded-xl bg-orange-500 py-4 text-lg font-semibold text-white transition hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? "Signing In…" : "Sign In"}
                </button>
              </form>

              <div className="my-8 flex items-center gap-4" aria-hidden="true">
                <span className="h-px flex-1 bg-gray-200" />
                <span className="text-sm text-gray-900">OR</span>
                <span className="h-px flex-1 bg-gray-200" />
              </div>

              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={loading}
                className="flex w-full items-center justify-center gap-3 rounded-xl border border-gray-300 py-4 font-semibold text-gray-950 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FcGoogle className="text-2xl" aria-hidden="true" />
                {loading ? "Please wait…" : "Continue with Google"}
              </button>
            </>
          )}
        </div>
      </section>
    </main>
  );
};

export default AdminLogin;
